import './assets/styles.css';
import React from 'react';
import ReactDOM from 'react-dom/client';
import ImageUploader from './components/ImageUploader';
import CompressionOptions from './components/CompressionOptions';
import ImageDisplay from './components/ImageDisplay';
import imageCompression from './utils/imageCompression';
import heicToJpeg from './utils/heicToJpeg';

const App = () => {
    const [uploadedImage, setUploadedImage] = React.useState(null);
    const [compressedImage, setCompressedImage] = React.useState(null);
    const [uploadedImageSize, setUploadedImageSize] = React.useState(null);
    const [compressedImageSize, setCompressedImageSize] = React.useState(null);
    const [errorMessage, setErrorMessage] = React.useState(null);
    const [isCompressionInProgress, setIsCompressionInProgress] = React.useState(false);
    const [isCompressionComplete, setIsCompressionComplete] = React.useState(false);
    const [isUploadInProgress, setIsUploadInProgress] = React.useState(false);

    const handleImageUpload = async (imageFile) => {
        setIsUploadInProgress(true); // Start upload progress
        try {
            let processedFile = imageFile;

            if (imageFile.type === 'image/heic' || imageFile.type === 'image/heif') {
                processedFile = await heicToJpeg(imageFile);
            }

            setUploadedImage(processedFile);
            setUploadedImageSize((processedFile.size / 1024).toFixed(2));
            setCompressedImage(URL.createObjectURL(processedFile));
            setCompressedImageSize((processedFile.size / 1024).toFixed(2));
            setErrorMessage(null);
        } catch (error) {
            setErrorMessage(`Failed to process the uploaded image: ${error.message}`);
        } finally {
            setIsUploadInProgress(false); // End upload progress
        }
    };

    const handleCompression = (desiredSize) => {
        if (uploadedImage) {
            setIsCompressionInProgress(true);
            setIsCompressionComplete(false);
            const currentSize = uploadedImage.size / 1024;

            if (desiredSize >= currentSize) {
                setCompressedImage(URL.createObjectURL(uploadedImage));
                setCompressedImageSize(currentSize.toFixed(2));
                setErrorMessage(null);
                setIsCompressionInProgress(false);
                setIsCompressionComplete(true);
                return;
            }

            imageCompression(uploadedImage, desiredSize)
                .then((compressedBlob) => {
                    const compressedSize = compressedBlob.size / 1024;
                    setCompressedImage(URL.createObjectURL(compressedBlob));
                    setCompressedImageSize(compressedSize.toFixed(2));

                    if (compressedSize > desiredSize) {
                        setErrorMessage('Maximum compression achieved. The image size could not be reduced further.');
                    } else {
                        setErrorMessage(null);
                    }
                    setIsCompressionInProgress(false);
                    setIsCompressionComplete(true);
                })
                .catch((error) => {
                    setErrorMessage(error.message); // Display the error message in the error box
                    setIsCompressionInProgress(false);
                });
        }
    };

    return (
        <div id="app" className="container">
            <header>
                <h1>Image Compressor</h1>
            </header>
            <main>
                {!uploadedImage && (
                    <section className="upload-section">
                        {isUploadInProgress ? (
                            <p className="uploading-status">Uploading image, please wait...</p> // Use CSS class
                        ) : (
                            <ImageUploader onImageUpload={handleImageUpload} />
                        )}
                    </section>
                )}
                {uploadedImage && (
                    <>
                        <div className="image-display-container">
                            <ImageDisplay
                                title="Uploaded"
                                imageSrc={URL.createObjectURL(uploadedImage)}
                                imageSize={uploadedImageSize}
                            />
                            <ImageDisplay
                                title="Compressed"
                                imageSrc={compressedImage}
                                imageSize={compressedImageSize}
                            />
                        </div>
                        {errorMessage && (
                            <div className="error-message">
                                <p>{errorMessage}</p>
                            </div>
                        )}
                        <CompressionOptions
                            onCompressionSubmit={handleCompression}
                            compressedImage={compressedImage}
                            uploadedImage={uploadedImage}
                            isCompressionInProgress={isCompressionInProgress}
                            isCompressionComplete={isCompressionComplete}
                        />
                    </>
                )}
            </main>
        </div>
    );
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<App />);